import React, { useEffect, useState } from 'react';
import { ERequestTypes, EStripeBankAccount, exists } from 'lib/types';
import LoadingState from 'components/LoadingState';
import { loadStripe } from '@stripe/stripe-js';
import api from 'api';
import { Modal } from 'lib/components/Modal';
import { getCustomerBankAccounts } from 'utils/userPaymentMethods';
import { logAndCaptureException, logAndCaptureMessage } from 'utils';
import { PlusSmallIcon } from '@heroicons/react/24/outline';
import { ColumnButton } from 'lib/components/ColumnButton';
import { EStripePaymentMethodUSBankAccount } from 'lib/types/stripe';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import ToastActions from 'redux/toast';
import { getOrCreateUserStripeId } from 'services/billing';
import { ColumnService } from 'lib/services/directory';
import { selectUser } from 'redux/auth';
import { Alert } from 'lib/components/Alert';
import { BankAccountVerification } from 'lib/enums';
import { STRIPE_VARS } from '../../../constants';
import SettingsHeader from '../SettingsHeader';
import trash from '../publisher/trash.svg';
import { AccountBalance, Check, Clock, ErrorOutline } from '../icons';

export default function UserSettingsBankAccounts() {
  const user = useAppSelector(selectUser);
  const dispatch = useAppDispatch();

  // modals
  const [showAuthorizationModal, setShowAuthorizationModal] =
    useState<boolean>();
  const [showUnlinkModal, setShowUnlinkModal] = useState<EStripeBankAccount>();
  const [
    showUnlinkModalBankAccountPaymentMethod,
    setShowUnlinkModalBankAccountPaymentMethod
  ] = useState<EStripePaymentMethodUSBankAccount>();
  const [
    bankAccountPaymentMethodStatuses,
    setBankAccountPaymentMethodStatuses
  ] = useState<string[]>([]);
  const [
    bankAccountClientSecretForAchAgreement,
    setBankAccountClientSecretForAchAgreement
  ] = useState<string>();

  const [error, setError] = useState<string>();

  const [loading, setLoading] = useState<boolean>();

  const [
    stripeBankAccountsPaymentMethods,
    setStripeBankAccountsPaymentMethods
  ] = useState<EStripePaymentMethodUSBankAccount[]>([]);

  const [stripeBankAccounts, setStripeBankAccounts] = useState<
    EStripeBankAccount[]
  >([]);

  const [stripeId, setStripeId] = useState<string>();

  const flashUnlinkedToast = () => {
    dispatch(
      ToastActions.toastSuccess({
        headerText: 'Success',
        bodyText: 'Bank account unlinked.'
      })
    );
  };

  const flashVerifiedToast = () => {
    dispatch(
      ToastActions.toastSuccess({
        headerText: 'Successfully verified!',
        bodyText:
          'You can now pay for your notices with the click of a button using this payment method.'
      })
    );
  };

  const handleDelete = async (bankAccount: EStripeBankAccount) => {
    setError(undefined);
    try {
      await withLoading(async () => {
        if (!stripeId) {
          throw new Error('Missing Stripe customer ID.');
        }

        const req: ERequestTypes['payments/delete-source'] = {
          stripeId,
          sourceId: bankAccount.id
        };
        const response = await api.safePost('payments/delete-source', req);
        if (response.error) {
          throw new Error(response.error);
        }

        // After deletion, re-fetch the user's bank accounts
        await fetchBankAccounts();
      });
      setShowUnlinkModal(undefined);
      flashUnlinkedToast();
    } catch (err) {
      logAndCaptureException(
        ColumnService.PAYMENTS,
        err,
        'Failed to delete legacy bank account source',
        { bankAccountId: bankAccount.id }
      );
      setShowUnlinkModal(undefined);
      setError(
        "Sorry, there was an error removing your bank account. Please contact help@column.us and we'll help you."
      );
    }
  };

  const handleDeleteBankAccountPaymentMethod = async (
    bankAccount: EStripePaymentMethodUSBankAccount
  ) => {
    setError(undefined);
    try {
      await withLoading(async () => {
        const deleteResponse = await api.safePost(
          'payments/delete-payment-method',
          {
            bankAccount: bankAccount.id
          }
        );
        if (deleteResponse.error || !deleteResponse.response) {
          throw new Error('Could not delete bank account payment method');
        }

        // After deletion, re-fetch the user's bank accounts
        await fetchBankAccounts();
      });
      setShowUnlinkModalBankAccountPaymentMethod(undefined);
      flashUnlinkedToast();
    } catch (err) {
      logAndCaptureException(
        ColumnService.PAYMENTS,
        err,
        'Could not delete bank account payment method',
        {
          bankAccount: bankAccount.id
        }
      );
      setShowUnlinkModalBankAccountPaymentMethod(undefined);
      setError(
        "Sorry, there was an error removing your bank account. Please contact help@column.us and we'll help you."
      );
    }
  };

  const bg = {
    yellow: 'bg-column-yellow-500 bg-opacity-40',
    gray: 'bg-gray-200',
    red: 'bg-red-400',
    blue: 'bg-blue-500 bg-opacity-25'
  };
  const icons = {
    check: <Check />,
    waiting: <Clock />,
    failed: <ErrorOutline />
  };
  const text = {
    grayDark: 'text-gray-900',
    gray: 'text-gray-800',
    white: 'text-white',
    blue: 'text-blue-600'
  };

  let statusText;
  let statusIcon;
  let chipColor = bg.gray;
  let textColor = text.gray;

  function renderStatus(bankAccount: EStripeBankAccount) {
    const statusValue = bankAccount.status;

    switch (statusValue) {
      case 'new':
        statusText = BankAccountVerification.microdepositsPending.label;
        statusIcon = null;
        chipColor = bg.gray;
        textColor = text.gray;
        break;
      case 'verification_failed':
      case 'errored':
        statusText = BankAccountVerification.verificationFailed.label;
        statusIcon = icons.waiting;
        chipColor = bg.yellow;
        textColor = text.grayDark;
        break;
      case 'verified':
      case 'validated':
        statusText = BankAccountVerification.verified.label;
        statusIcon = icons.check;
        chipColor = bg.blue;
        textColor = text.blue;
        break;
      default:
        statusText = 'Unknown';
        statusIcon = icons.waiting;
        chipColor = bg.gray;
        textColor = text.gray;
        break;
    }
    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold justify-center ${chipColor} ${textColor}`}
      >
        {statusIcon && <span className="mr-2">{statusIcon}</span>}
        {statusText}
      </span>
    );
  }

  const retrieveStatusFromBankAccountPaymentMethod = async (
    bankAccount: EStripePaymentMethodUSBankAccount
  ) => {
    const { status } = await api.post(
      'payments/get-bank-account-payment-method-status',
      {
        payment_method: bankAccount.id
      }
    );

    return status;
  };

  function renderStatusBankAccountPaymentMethod(statusValue: string) {
    switch (statusValue) {
      case 'processing':
        statusText = BankAccountVerification.microdepositsPending.label;
        statusIcon = null;
        chipColor = bg.gray;
        textColor = text.gray;
        break;
      case 'requires_action':
      case 'canceled':
        statusText = BankAccountVerification.actionRequired.label;
        statusIcon = icons.waiting;
        chipColor = bg.yellow;
        textColor = text.grayDark;
        break;
      case 'succeeded':
        statusText = BankAccountVerification.verified.label;
        statusIcon = icons.check;
        chipColor = bg.blue;
        textColor = text.blue;
        break;
      default:
        statusText = 'Unknown';
        statusIcon = icons.waiting;
        chipColor = bg.gray;
        textColor = text.gray;
        break;
    }
    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold justify-center ${chipColor} ${textColor}`}
      >
        {statusIcon && <span className="mr-2">{statusIcon}</span>}
        {statusText}
      </span>
    );
  }

  const handleAccountAddedViaStripeUIFlow = async () => {
    const stripe = await loadStripe(STRIPE_VARS.key);

    if (!stripe) {
      throw new Error(
        'Stripe.js failed to load. Please disable any ad-blockers and try again.'
      );
    }

    const confirmBankAccount = await stripe.confirmUsBankAccountSetup(
      bankAccountClientSecretForAchAgreement || ''
    );

    if (confirmBankAccount.error) {
      throw new Error(
        confirmBankAccount.error.message ||
          'Could not confirm your bank account. Please try again.'
      );
    }

    setShowAuthorizationModal(false);

    if (confirmBankAccount.setupIntent?.status === 'succeeded') {
      await api.post('payments/attach-payment-method-to-customer', {
        payment_method: confirmBankAccount.setupIntent?.payment_method,
        customer: stripeId
      });
      flashVerifiedToast();
    }
  };

  /**
   * Get the user's bank accounts from Stripe.
   */
  const fetchBankAccounts = async () => {
    if (!stripeId) {
      logAndCaptureMessage('stripeId is not defined in fetchBankAccounts');
      return;
    }

    if (!exists(user)) {
      return;
    }

    try {
      const { bankAccounts, bankAccountsPaymentMethods } =
        await getCustomerBankAccounts(user);

      setStripeBankAccountsPaymentMethods(bankAccountsPaymentMethods || []);
      if (bankAccountsPaymentMethods.length > 0) {
        const statuses = await Promise.all(
          bankAccountsPaymentMethods.map(bankAccount => {
            return retrieveStatusFromBankAccountPaymentMethod(bankAccount);
          })
        );
        setBankAccountPaymentMethodStatuses(statuses);
      }
      setStripeBankAccounts(bankAccounts || []);
    } catch (e) {
      logAndCaptureException(
        ColumnService.PAYMENTS,
        e,
        'Failed to fetch bank accounts for user',
        { stripeId }
      );
      setStripeBankAccountsPaymentMethods([]);
      setStripeBankAccounts([]);
      setError(
        'There was an error loading your bank accounts. Try refreshing the page.'
      );
    }
  };

  const launchFinancialConnectionsSession = async () => {
    setError(undefined);
    try {
      const { clientSecret } = await api.post(
        'payments/stripe-create-setup-intent',
        {
          customer: stripeId
        }
      );

      const stripePromise = loadStripe(STRIPE_VARS.key);
      const stripe = await stripePromise;

      if (!stripe) {
        throw new Error('stripe undefined');
      }

      const { name, email } = await api.get(
        'payments/stripe-customer-details',
        {
          customer: stripeId
        }
      );

      const result = await stripe.collectBankAccountForSetup({
        clientSecret,
        params: {
          payment_method_type: 'us_bank_account',
          payment_method_data: {
            billing_details: {
              name,
              email
            }
          }
        },
        expand: ['payment_method']
      });

      if (result.error) {
        logAndCaptureException(
          ColumnService.PAYMENTS,
          result.error,
          'Stripe collectBankAccountForSetup method failed',
          {
            user: user?.id
          }
        );
        setError(
          "There was an issue adding your bank account, but it should work if you try again. If the problem persists, contact help@column.us and we'll help you add it."
        );
        return;
      }

      if (result.setupIntent?.status !== 'requires_payment_method') {
        setBankAccountClientSecretForAchAgreement(
          result.setupIntent?.client_secret || ''
        );
        setShowAuthorizationModal(true);
      }
    } catch (e) {
      logAndCaptureException(
        ColumnService.PAYMENTS,
        e,
        'An error occurred when going through UI flow to add bank account for user ',
        {
          user: user?.id
        }
      );
      setError(
        "There was an issue adding your bank account. If the problem persists, contact help@column.us and we'll help you add it."
      );
    }
  };

  /**
   * Helper to set loading state while a promise runs.
   */
  function withLoading<T>(fn: () => Promise<T>) {
    setLoading(true);
    return fn().finally(() => setLoading(false));
  }

  useEffect(() => {
    if (!stripeId) {
      return;
    }
    void fetchBankAccounts();
  }, [stripeId]);

  useEffect(() => {
    const fetchStripeId = async () => {
      if (!exists(user)) {
        return;
      }
      try {
        const activeOrganization = await user.data().activeOrganization?.get();
        const stripeIdResult = await getOrCreateUserStripeId(
          user,
          activeOrganization
        );
        if (!stripeIdResult) {
          logAndCaptureMessage(
            `Could not locate stripe id for user ${user.id}`
          );
          setError(
            'There was an error setting up your payment profile. Please refresh the page or contact help@column.us.'
          );
          return;
        }
        setStripeId(stripeIdResult);
      } catch (e) {
        logAndCaptureException(
          ColumnService.PAYMENTS,
          e,
          'Failed to get or create Stripe ID for user',
          { userId: user.id }
        );
        setError(
          'There was an error setting up your payment profile. Please refresh the page or contact help@column.us.'
        );
      }
    };

    void withLoading(() => fetchStripeId());
  }, [user?.id, user?.data().activeOrganization?.id]);

  if (!user) return <LoadingState />;

  return (
    <div className="bg-white sm:rounded-lg border border-gray-300 shadow">
      {error && <Alert id="bank-account-error" status="error" title={error} />}
      <SettingsHeader
        header="Bank Accounts"
        description="Link your bank for direct payments."
      >
        <ColumnButton
          tertiary
          id="invite"
          size="lg"
          buttonText={'Add bank account'}
          onClick={() => {
            void launchFinancialConnectionsSession();
          }}
          startIcon={<PlusSmallIcon className="w-5 h-5 text-gray-750" />}
          type="button"
        />
      </SettingsHeader>
      <>
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr>
              <th className="bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                {' '}
              </th>
              <th className="pl-2 pr-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                account number
              </th>
              <th className="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                account name
              </th>
              <th className="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                status
              </th>
              <th className="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                actions
              </th>
            </tr>
          </thead>
          {!loading && (
            <tbody className="divide-y divide-gray-200 rounded-b-lg">
              {stripeBankAccounts &&
                stripeBankAccounts.map(
                  (bankAccount: EStripeBankAccount, index: number) => {
                    return (
                      <tr key={index} className="hover:bg-gray-100">
                        <td className="pl-6 pr-2 py-4 whitespace-no-wrap text-sm leading-5 text-gray-900">
                          <div className="flex items-center justify-center bg-column-yellow-500 w-10 h-10 rounded-circle">
                            <AccountBalance />
                          </div>
                        </td>
                        <td className="pl-2 pr-6 py-4 whitespace-no-wrap text-sm leading-5 text-gray-900">
                          {`**** **** ${bankAccount.last4}`}
                        </td>
                        <td className="px-6 py-4 whitespace-no-wrap text-sm leading-5 text-gray-900">
                          {bankAccount.bank_name || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-no-wrap text-sm leading-5 text-gray-900">
                          <>{renderStatus(bankAccount)}</>
                        </td>
                        <td className="flex items-end px-6 py-6 whitespace-no-wrap text-sm leading-5 font-medium text-gray-900">
                          <div
                            onClick={e => {
                              e.stopPropagation();
                              setShowUnlinkModal(bankAccount);
                            }}
                            className="cursor-pointer ml-4"
                            id={`delete-${index}`}
                          >
                            <img src={trash} alt="trash" />
                          </div>
                        </td>
                      </tr>
                    );
                  }
                )}
              {stripeBankAccountsPaymentMethods &&
                stripeBankAccountsPaymentMethods.map(
                  (
                    bankAccount: EStripePaymentMethodUSBankAccount,
                    index: number
                  ) => {
                    return (
                      <tr
                        key={index}
                        className={`
                          hover:bg-gray-100 cursor-pointer`}
                      >
                        <td className="pl-6 pr-2 py-4 whitespace-no-wrap text-sm leading-5 text-gray-900">
                          <div className="flex items-center justify-center bg-column-yellow-500 w-10 h-10 rounded-circle">
                            <AccountBalance />
                          </div>
                        </td>
                        <td className="pl-2 pr-6 py-4 whitespace-no-wrap text-sm leading-5 text-gray-900">
                          {`**** **** ${
                            bankAccount.us_bank_account?.last4 || 'n/a'
                          }`}
                        </td>
                        <td className="px-6 py-4 whitespace-no-wrap text-sm leading-5 text-gray-900">
                          {bankAccount.us_bank_account?.bank_name || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-no-wrap text-sm leading-5 text-gray-900">
                          {renderStatusBankAccountPaymentMethod(
                            bankAccountPaymentMethodStatuses[index]
                          )}
                        </td>
                        <td className="flex items-end px-6 py-6 whitespace-no-wrap text-sm leading-5 font-medium text-gray-900">
                          <div
                            onClick={e => {
                              e.stopPropagation();
                              setShowUnlinkModalBankAccountPaymentMethod(
                                bankAccount
                              );
                            }}
                            className="cursor-pointer ml-4"
                            id={`delete-${index}`}
                          >
                            <img src={trash} alt="trash" />
                          </div>
                        </td>
                      </tr>
                    );
                  }
                )}
            </tbody>
          )}
        </table>
        {!stripeBankAccounts &&
          !stripeBankAccountsPaymentMethods &&
          !loading && (
            <div className="h-12 flex items-center justify-center bg-white rounded-b">
              No records to display
            </div>
          )}
        {loading && (
          <div className="h-12 flex items-center justify-center bg-white rounded-b">
            <div className="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-5 w-5 mr-2" />
          </div>
        )}
      </>
      {showAuthorizationModal && (
        <Modal
          id="ach-authorization-modal"
          title="ACH authorization agreement"
          onClose={() => setShowAuthorizationModal(false)}
          allowCloseOutsideModal={false}
          size="md"
          primaryAction={{
            id: 'confirm-authorization',
            buttonText: 'Confirm',
            type: 'button',
            loading: !!loading,
            onClick: async () => {
              setLoading(true);
              try {
                await handleAccountAddedViaStripeUIFlow();
                await withLoading(() => fetchBankAccounts());
              } catch (err) {
                logAndCaptureException(
                  ColumnService.PAYMENTS,
                  err,
                  'Failed to confirm bank account via Stripe UI flow'
                );
                setShowAuthorizationModal(false);
                setError(
                  err instanceof Error
                    ? err.message
                    : 'Could not confirm your bank account. Please try again.'
                );
              } finally {
                setLoading(false);
              }
            }
          }}
        >
          <div className="mb-1 mt-2">
            <div className="bg-gray-300 bg-opacity-50">
              <div className="p-4 text-xs mb-1">
                I hereby authorize and request Column, PBC. to debit funds from
                my account at the Financial Institution indicated, and credit
                the funds. I authorize Column, PBC. to take any and all action
                required to correct any errors.
                <br />
                <br /> By clicking the button below, I certify that the
                information I have given on this ACH Debit Authorization
                Agreement for Direct Payments is complete, true, and submitted
                for the purpose selected above. It will remain in effect until I
                notify Column, PBC. of its cancellation by contacting
                help@column.us or by unlinking the bank account in my Column
                settings page.
              </div>
            </div>
            <div className="flex items-center mb-4 mt-3">
              <input
                id="agreeToTerms"
                type="checkbox"
                className="form-checkbox h-4 w-4 text-gray-600 transition duration-150 ease-in-out"
                checked
              />
              <div className="ml-2 block text-sm leading-4 text-gray-900">
                I agree to the authorization terms above.
              </div>
            </div>
          </div>
        </Modal>
      )}
      {showUnlinkModal && (
        <Modal
          id="unlink-bank-account-modal"
          title="Unlink bank account?"
          onClose={() => setShowUnlinkModal(undefined)}
          allowCloseOutsideModal={false}
          primaryAction={{
            buttonText: 'Unlink',
            onClick: () => handleDelete(showUnlinkModal),
            type: 'button',
            loading: !!loading
          }}
        >
          <p>
            This bank account will be deleted from your saved payment methods on
            Column.
          </p>
        </Modal>
      )}
      {showUnlinkModalBankAccountPaymentMethod && (
        <Modal
          id="unlink-payment-method-modal"
          title="Unlink bank account?"
          onClose={() => setShowUnlinkModalBankAccountPaymentMethod(undefined)}
          allowCloseOutsideModal={false}
          primaryAction={{
            buttonText: 'Unlink',
            onClick: () =>
              handleDeleteBankAccountPaymentMethod(
                showUnlinkModalBankAccountPaymentMethod
              ),
            type: 'button',
            loading: !!loading
          }}
        >
          <p>
            This bank account will be deleted from your saved payment methods on
            Column.
          </p>
        </Modal>
      )}
    </div>
  );
}
